/* TMA auth client (DESIGN14 §F1.3).

   The mini app authenticates by POSTing the Telegram initData to
   /api/tma/auth. The backend verifies the HMAC and issues the SAME session
   JWT as a normal web login. We then SEED that token into the exact storage
   key the global fetchInterceptor reads (`lato.auth.token.v1`), so every
   existing API client (architectApi / estimateApi / proposalApi) works
   inside the TMA with zero forking — the token rides along automatically.

   Mounting the real <AuthProvider> after seeding lets the whole surface use
   the existing useAuth() hook (the mechanism the SPA already uses), so the
   role helpers estimateAccess / proposalAccess get their user + permissions
   the normal way. */

import { useCallback, useEffect, useState } from 'react';
import { AuthedUser } from '../auth/api';
import { setSessionToken } from '../auth/sessionToken';
import {
  approvalsApi,
  ApprovalStatus,
  ApprovalStep,
  ApprovalTargetKind,
} from '../architect/api';
import { fmtDate } from '../architect/utils';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(
  /\/+$/,
  '',
);

export interface TmaAuthOk {
  ok: true;
  token: string;
  user: AuthedUser;
  permissions: string[];
}

/** Error `code` is the backend's machine code so the UI can branch:
    'tma_not_configured' (503) · 'bad_init_data' · 'not_linked' ·
    'tma_not_allowed' · 'user_disabled' · 'network' · anything else. */
export interface TmaAuthErr {
  ok: false;
  status: number;
  code: string;
}

export type TmaAuthResult = TmaAuthOk | TmaAuthErr;

/** POST the initData. On success, seed the shared token store and return the
    session. Never throws — the caller renders a friendly state per `code`. */
export async function authTma(initData: string): Promise<TmaAuthResult> {
  let res: Response;
  try {
    res = await fetch(`${API}/api/tma/auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
  } catch {
    return { ok: false, status: 0, code: 'network' };
  }
  let data: {
    token?: string;
    user?: AuthedUser;
    permissions?: string[];
    error?: string;
  } = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok || !data.token || !data.user) {
    return {
      ok: false,
      status: res.status,
      code: data.error || (res.status === 503 ? 'tma_not_configured' : 'auth_failed'),
    };
  }
  setSessionToken(data.token);
  return {
    ok: true,
    token: data.token,
    user: data.user,
    permissions: data.permissions || [],
  };
}

/** Drop the seeded token (used only if a fatal auth error occurs after a
    previous session was cached). */
export function clearTmaToken(): void {
  setSessionToken(null);
}

/* ═══════════ APPROVALS FOR THE MINI APP (DESIGN24 §T1.4) ═══════════
   Stage 1 (DESIGN23) made every approve step policy-driven: a step can now
   demand several named approvers, so an approve control must be able to say
   «Approved 1 of 2 · 14:12», go quiet with «You approved», or disappear
   entirely for someone who is not an approver.

   The mini app consumes the SAME `/api/approvals` client as the web — no
   forked endpoints — but it cannot reuse the web's <ApprovalNote>, whose
   markup carries `arch-*` classes that only exist in the web stylesheet. The
   helpers below are therefore the mini app's shared, PRESENTATION-FREE core:
   they answer «may I approve?», «what does the counter read?», and hand back
   plain strings each screen renders in its own Telegram-native chrome.

   THE RULE THAT OUTRANKS EVERYTHING (inherited verbatim from the web): with
   NO policy configured the surface must behave EXACTLY as it did before
   stage 1. A missing / failed status therefore reads as «no policy» — never
   as a hidden button — and a counter is only drawn once it actually says
   something («Approved 0 of 1» is never printed).

   Shared with T2's document tabs: the estimate and proposal approve controls
   consume these same helpers, so all three steps speak one dialect. */

/** May this viewer press approve right now? An absent status (no policy, an
    older backend, a transient failure) reads as YES — today's behaviour. */
export function canApproveNow(status?: ApprovalStatus | null): boolean {
  return status ? status.me.can_approve : true;
}

/** This viewer's own approval time, or null — drives the quiet «You approved»
    state that replaces the button. */
export function myApprovalTime(
  status?: ApprovalStatus | null,
  userId?: string | null,
): string | null {
  if (!status || !userId) return null;
  if (!status.me.has_approved) return null;
  const row = (status.approvals || []).find((a) => a.user_id === userId);
  return row ? row.approved_at : null;
}

/** The most recent vote's timestamp (ISO), or null. */
export function lastApprovalTime(status?: ApprovalStatus | null): string | null {
  const rows = (status?.approvals || []).filter((a) => !!a.approved_at);
  if (!rows.length) return null;
  return rows
    .slice()
    .sort((a, b) => (a.approved_at < b.approved_at ? 1 : -1))[0].approved_at;
}

/** Every vote as one line — «Name · 30/07, 14:12», or the time alone when the
    viewer may not see WHO approved (the estimate step keeps its root-only
    identity rule; the TIME is public on every step). */
export function approvalTooltip(status?: ApprovalStatus | null): string {
  const rows = status?.approvals || [];
  if (!rows.length) return 'No approvals yet';
  return rows
    .map((a) => {
      const when = fmtDate(a.approved_at);
      const who = a.user_name || 'Approved';
      return when ? who + ' · ' + when : who;
    })
    .join('\n');
}

/** What an approve control should SAY, as plain strings. `null` overall means
    «render nothing» — the no-policy default, where the screen looks exactly as
    it did before stage 1. */
export interface ApprovalLines {
  /** «Approved 1 of 2 · 30/07, 14:12» — the progress counter. */
  meter: string | null;
  /** True once the threshold is met (styles the counter as satisfied). */
  met: boolean;
  /** «You approved · 30/07, 14:12» — this viewer's own quiet state. */
  mine: string | null;
  /** «Waiting for the assigned approvers — Ann, Bob» for a non-approver. */
  waiting: string | null;
  /** Every vote, one per line (used as a title/long-press hint). */
  tooltip: string;
}

/**
 * Turn a status into the three lines a mini-app approve control can render.
 *
 * @param pending  Is this step still AWAITING approval? Only a pending step
 *                 may say who it is waiting for — an approved document must
 *                 never read as if its approval had been taken away.
 */
export function approvalLines(
  status: ApprovalStatus | null | undefined,
  userId: string | null | undefined,
  pending: boolean,
): ApprovalLines | null {
  if (!status) return null;
  /* The counter is drawn only when it SAYS something:
       • while a step is still pending, a multi-approval policy is worth
         showing from «0 of 2» — that IS the news;
       • once a step is settled, only real votes are shown. A document
         approved BEFORE the policy existed carries no votes, and printing
         «Approved 0 of 2» under its approved chip would read as if the
         approval had been withdrawn. It never is. */
  const showMeter = status.given > 0 || (pending && status.required > 1);
  const mineAt = pending ? myApprovalTime(status, userId) : null;
  // The server's turn is authoritative for ordered policies. Show it even to
  // the active reviewer so the next action is obvious on a narrow screen.
  const waiting = pending && !!status.turn
    ? status.turn.id === userId && status.me.can_approve
      ? 'Your turn to approve'
      : 'Waiting for ' + status.turn.name + ' to approve'
    : pending && !status.me.can_approve
      ? 'Waiting for the assigned approvers' +
        ((status.approvers || []).length ? ' — ' + (status.approvers || []).map(u => u.display_name || u.username).filter(Boolean).join(', ') : '')
      : null;
  if (!showMeter && !mineAt && !waiting) return null;

  const last = fmtDate(lastApprovalTime(status));
  return {
    meter: showMeter
      ? `Approved ${status.given} of ${status.required}` + (last ? ' · ' + last : '')
      : null,
    met: status.given >= status.required,
    mine: mineAt
      ? 'You approved' + (fmtDate(mineAt) ? ' · ' + fmtDate(mineAt) : '')
      : null,
    waiting,
    tooltip: approvalTooltip(status),
  };
}

/** Live approval status for ONE target, refreshed on demand and (optionally)
    on a slow timer so a colleague's vote in a 2-of-2 policy appears on its
    own. Silent on failure BY DESIGN: no status means «no policy», which is
    exactly today's behaviour, and a background read must never raise an error
    in the operator's face.

    @param pollMs  0 (default) = fetch once per key change; > 0 also re-reads
                   on that interval. Callers poll only while a multi-approver
                   step is actually pending. */
export function useApprovalStatus(
  step: ApprovalStep,
  targetKind: ApprovalTargetKind,
  targetId: string | null | undefined,
  enabled: boolean,
  pollMs = 0,
): { status: ApprovalStatus | null; reload: () => void } {
  const [status, setStatus] = useState<ApprovalStatus | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !targetId) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    const read = async () => {
      try {
        const st = await approvalsApi.getStatus({
          step,
          target_kind: targetKind,
          target_id: targetId,
        });
        if (!cancelled) setStatus(st);
      } catch {
        /* keep whatever we had — never an error state, never a hidden button */
      }
    };
    void read();
    if (!pollMs) {
      return () => {
        cancelled = true;
      };
    }
    const t = window.setInterval(() => void read(), pollMs);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [step, targetKind, targetId, enabled, pollMs, tick]);

  return { status, reload };
}
